import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, AlertCircle, ExternalLink, Check } from "lucide-react";

/**
 * Fase 6.9 · Admin · vista dei mismatch tra producer nel DB e producer
 * nel campo `nota` di Mexal.
 *
 * L'endpoint `sync-producers` (default) NON sovrascrive i producer già
 * valorizzati (per non buttare via l'inferenza AI o il lavoro manuale
 * dell'admin). Qui elenchiamo tutte le divergenze così il cliente decide
 * caso per caso: "APPLICA Mexal", oppure "MANTIENI attuale" (nessuna
 * azione), oppure "riscrivi manualmente" (link alla scheda).
 */
export default function AdminProducerMismatches() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/wines/producer-mismatches", { timeout: 240_000 });
            setData(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore caricamento mismatch");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const apply = async (item) => {
        setApplying(item.id);
        try {
            await api.post(`/admin/wines/${item.id}/producer`, { producer: item.mexal_producer });
            toast.success(`${item.sku}: applicato "${item.mexal_producer}"`);
            setData({
                ...data,
                items: data.items.filter((x) => x.id !== item.id),
                total: (data.total || 1) - 1,
            });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore aggiornamento");
        } finally {
            setApplying(null);
        }
    };

    return (
        <div className="p-6 max-w-6xl mx-auto" data-testid="admin-producer-mismatches-page">
            <div className="flex items-center justify-between mb-4 gap-3">
                <div>
                    <h1 className="text-2xl flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" strokeWidth={1.5} />
                        Mismatch produttore Mexal
                    </h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
                        Vini per cui il produttore attuale (da AI o admin) è
                        diverso da quello memorizzato in Mexal (campo <code>nota</code>).
                        Il sync automatico non li ha toccati. Decidi caso per caso.
                    </p>
                </div>
                <button className="co-btn-ghost" onClick={load} disabled={loading} data-testid="mismatch-refresh">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    {loading ? "Ricarico…" : "Ricarica"}
                </button>
            </div>

            {!data ? (
                <div className="text-sm text-[hsl(var(--muted-foreground))]">Caricamento…</div>
            ) : data.items.length === 0 ? (
                <div className="p-6 bg-[#FDFBF7] border border-[hsl(var(--border))] rounded-sm text-center" data-testid="mismatch-empty">
                    Nessun mismatch — perfetta coerenza tra DB e Mexal.
                </div>
            ) : (
                <>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3" data-testid="mismatch-total">
                        Trovati <b>{data.total}</b> vini con producer diverso da Mexal.
                    </p>
                    <div className="bg-white border border-[hsl(var(--border))] rounded-sm overflow-x-auto">
                        <table className="w-full text-sm" data-testid="mismatch-table">
                            <thead className="bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] text-[11px] uppercase tracking-wider">
                                <tr>
                                    <th className="text-left p-3">SKU · Vino</th>
                                    <th className="text-left p-3">Producer attuale</th>
                                    <th className="text-left p-3">Producer Mexal</th>
                                    <th className="text-right p-3">Azione</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[hsl(var(--border))]">
                                {data.items.map((it) => (
                                    <tr key={it.id} data-testid={`mismatch-row-${it.id}`}>
                                        <td className="p-3">
                                            <div className="font-mono text-xs">{it.sku}</div>
                                            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{it.name}</div>
                                        </td>
                                        <td className="p-3 italic">{it.current_producer || "—"}</td>
                                        <td className="p-3 font-medium">{it.mexal_producer}</td>
                                        <td className="p-3 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => apply(it)}
                                                    disabled={applying === it.id}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-sm hover:opacity-90 disabled:opacity-50"
                                                    data-testid={`mismatch-apply-${it.id}`}
                                                    title="Sovrascrivi con valore Mexal"
                                                >
                                                    <Check className="w-3 h-3" strokeWidth={1.5} />
                                                    {applying === it.id ? "…" : "Usa Mexal"}
                                                </button>
                                                <Link
                                                    to={`/admin/vini/${it.id}`}
                                                    className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                                                    data-testid={`mismatch-edit-${it.id}`}
                                                >
                                                    Scheda <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
